import { useState } from 'react';
import { Coins, TrendingUp, Zap, AlertCircle } from 'lucide-react';
import { StatCardsSkeleton, TableSkeleton } from '@/components/feedback/Skeletons';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { StatCard } from '@/components/stats/StatCard';
import { DailyChart } from '@/components/stats/DailyChart';
import { useUsageStats } from '@/api/queries';
import { formatEur, formatTokens, durationLabel, relativeTime } from '@/lib/format';
import { cn } from '@/lib/cn';

type Range = '7d' | '30d' | '90d' | 'all';

const RANGES: { value: Range; label: string }[] = [
  { value: '7d', label: '7 days' },
  { value: '30d', label: '30 days' },
  { value: '90d', label: '90 days' },
  { value: 'all', label: 'All time' },
];

export function UsagePage() {
  const [range, setRange] = useState<Range>('30d');
  const { data } = useUsageStats(range);

  const totalTokens = data ? data.totals.tokens_in + data.totals.tokens_out : null;
  const maxAgentCost = data ? Math.max(...data.by_agent.map((a) => a.cost_eur), 0.000001) : 1;

  return (
    <div className="mx-auto flex max-w-6xl flex-col gap-6">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Usage &amp; Costs</h1>
          <p className="text-sm text-muted-foreground">
            Token consumption and estimated costs across all agents.
          </p>
        </div>
        <RangeSelector value={range} onChange={setRange} />
      </header>

      {/* Stat cards */}
      <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
        {!data ? (
          <StatCardsSkeleton />
        ) : (
          <>
            <StatCard
              icon={<Zap className="h-4 w-4" />}
              label="Total runs"
              value={data.totals.runs.toLocaleString()}
              sub={`${data.totals.failed} failed`}
              warn={data.totals.failed > 0}
            />
            <StatCard
              icon={<TrendingUp className="h-4 w-4" />}
              label="Tokens in"
              value={formatTokens(data.totals.tokens_in)}
              sub={`out: ${formatTokens(data.totals.tokens_out)}`}
            />
            <StatCard
              icon={<TrendingUp className="h-4 w-4" />}
              label="Total tokens"
              value={formatTokens(totalTokens)}
            />
            <StatCard
              icon={<Coins className="h-4 w-4" />}
              label="Cost (EUR)"
              value={formatEur(data.totals.cost_eur)}
              sub={`≈ $${data.totals.cost_usd.toFixed(4)}`}
              accent
            />
          </>
        )}
      </div>

      {/* Daily chart */}
      {data && data.by_day.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-sm font-medium">Daily cost (EUR)</CardTitle>
          </CardHeader>
          <CardContent>
            <DailyChart days={data.by_day} />
            <p className="mt-2 text-right text-xs text-muted-foreground">
              Rate: 1 USD ≈ {data.eur_rate} EUR (fixed)
            </p>
          </CardContent>
        </Card>
      )}

      {/* Per-agent breakdown */}
      <Card>
        <CardHeader>
          <CardTitle>By agent</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {!data ? (
            <TableSkeleton
              widths={['w-28', 'w-14', 'w-20', 'w-20', 'w-20', 'w-20', 'w-24', 'w-24']}
              rows={5}
              bordered={false}
            />
          ) : data.by_agent.length === 0 ? (
            <div className="p-6 text-sm text-muted-foreground">No runs recorded in this period.</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-muted/30 text-left text-xs uppercase tracking-wider text-muted-foreground">
                <tr>
                  <th className="px-4 py-2">Agent</th>
                  <th className="px-4 py-2 text-right">Runs</th>
                  <th className="px-4 py-2 text-right">Tokens in</th>
                  <th className="px-4 py-2 text-right">Tokens out</th>
                  <th className="px-4 py-2 text-right">Avg duration</th>
                  <th className="px-4 py-2 text-right">Cost (EUR)</th>
                  <th className="px-4 py-2">Share</th>
                  <th className="px-4 py-2 text-right">Last run</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {data.by_agent.map((a) => (
                  <tr key={a.agent_name} className="hover:bg-muted/20">
                    <td className="px-4 py-2 font-medium">
                      <span className="flex items-center gap-1.5">
                        {a.failed > 0 && (
                          <AlertCircle className="h-3.5 w-3.5 shrink-0 text-destructive" />
                        )}
                        {a.agent_name}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-right tabular-nums">
                      {a.runs.toLocaleString()}
                      {a.failed > 0 && (
                        <span className="ml-1 text-xs text-destructive">({a.failed}✕)</span>
                      )}
                    </td>
                    <td className="px-4 py-2 text-right tabular-nums text-muted-foreground">
                      {formatTokens(a.tokens_in)}
                    </td>
                    <td className="px-4 py-2 text-right tabular-nums text-muted-foreground">
                      {formatTokens(a.tokens_out)}
                    </td>
                    <td className="px-4 py-2 text-right tabular-nums text-muted-foreground">
                      {durationLabel(a.avg_duration_ms)}
                    </td>
                    <td className="px-4 py-2 text-right tabular-nums font-medium">
                      {formatEur(a.cost_eur)}
                    </td>
                    <td className="px-4 py-2">
                      <CostBar fraction={a.cost_eur / maxAgentCost} />
                    </td>
                    <td className="px-4 py-2 text-right text-xs text-muted-foreground">
                      {relativeTime(a.last_run_at)}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="border-t bg-muted/20 text-xs font-medium text-muted-foreground">
                <tr>
                  <td className="px-4 py-2">Total</td>
                  <td className="px-4 py-2 text-right tabular-nums">
                    {data.totals.runs.toLocaleString()}
                  </td>
                  <td className="px-4 py-2 text-right tabular-nums">
                    {formatTokens(data.totals.tokens_in)}
                  </td>
                  <td className="px-4 py-2 text-right tabular-nums">
                    {formatTokens(data.totals.tokens_out)}
                  </td>
                  <td />
                  <td className="px-4 py-2 text-right tabular-nums text-foreground">
                    {formatEur(data.totals.cost_eur)}
                  </td>
                  <td colSpan={2} />
                </tr>
              </tfoot>
            </table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function RangeSelector({ value, onChange }: { value: Range; onChange: (r: Range) => void }) {
  return (
    <div className="flex gap-1 rounded-lg border bg-muted/30 p-1">
      {RANGES.map((r) => (
        <button
          key={r.value}
          onClick={() => onChange(r.value)}
          className={cn(
            'rounded-md px-3 py-1 text-xs font-medium transition-colors',
            value === r.value
              ? 'bg-background text-foreground shadow-sm'
              : 'text-muted-foreground hover:text-foreground',
          )}
        >
          {r.label}
        </button>
      ))}
    </div>
  );
}

function CostBar({ fraction }: { fraction: number }) {
  const pct = Math.max(2, Math.round(fraction * 100));
  return (
    <div className="h-2 w-24 overflow-hidden rounded-full bg-muted">
      <div
        className="h-full rounded-full bg-primary/70 transition-all"
        style={{ width: `${pct}%` }}
      />
    </div>
  );
}
